import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Checkbox,
  CrudMode,
  DataArray,
  DataDisplay,
  formatBoolean,
  Grid,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { AddressFields, Input, Select } from "components";
import { Address, School, SchoolEditResource, SchoolViewResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const SchoolPageBody = (props: {
  crudMode: CrudMode;
  school: School;
  setSchool: (value: School) => void;
  viewResource: SchoolViewResource;
  editResource: SchoolEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const nameField = () => {
    const propertyName = pn<School>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.school.name}</DataDisplay>
    ) : (
      <Input
        
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.school.name}
        onChange={(e) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const officialSchoolNameField = () => {
    const propertyName = pn<School>("officialSchoolName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.school.officialSchoolName}</DataDisplay>
    ) : (
      <Input
        
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.school.officialSchoolName}
        onChange={(e) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.officialSchoolName = e.target.value;
            })
          )
        }
      />
    );
  };

  const schoolCodeField = () => {
    const propertyName = pn<School>("schoolCode");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.school.schoolCode}</DataDisplay>
    ) : (
      <Input
        
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.school.schoolCode}
        onChange={(e) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.schoolCode = e.target.value;
            })
          )
        }
      />
    );
  };

  const schoolTypeField = () => {
    const propertyName = pn<School>("schoolTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.school.schoolType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schoolTypes?.find((x) => x.id === props.school.schoolTypeId)}
        options={props.editResource.schoolTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.schoolTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const ncesField = () => {
    const propertyName = pn<School>("nces");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.school.nces}</DataDisplay>
    ) : (
      <Input
        
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.school.nces}
        onChange={(e) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.nces = e.target.value;
            })
          )
        }
      />
    );
  };

  const actCodeField = () => {
    const propertyName = pn<School>("actCode");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.school.actCode}</DataDisplay>
    ) : (
      <Input
        
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.school.actCode}
        onChange={(e) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.actCode = e.target.value;
            })
          )
        }
      />
    );
  };

  const termTypeField = () => {
    const propertyName = pn<School>("termTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.school.termType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.termTypes?.find((x) => x.id === props.school.termTypeId)}
        options={props.editResource.termTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.termTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const gradeLevelsField = () => {
    const propertyName = pn<School>("gradeLevels");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.school.gradeLevels?.map((x) => x.name)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.school.gradeLevels}
        options={props.editResource.gradeLevels}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.gradeLevels = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const contactField = () => {
    const propertyName = pn<School>("contactId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.school.contactName}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.contacts?.find((x) => x.id === props.school.contactId)}
        options={props.editResource.contacts}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.contactId = value?.id;
            })
          )
        }
      />
    );
  };

  const serviceLocationOnlyField = () => {
    const propertyName = pn<School>("serviceLocationOnly");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.school.serviceLocationOnly)}</DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.school.serviceLocationOnly}
        onChange={(e) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.serviceLocationOnly = e.target.checked;
            })
          )
        }
      />
    );
  };

  const isTitle1Field = () => {
    const propertyName = pn<School>("isTitle1");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.school.isTitle1)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.school.isTitle1)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.isTitle1 = value?.id;
            })
          )
        }
      />
    );
  };

  const inactiveField = () => {
    const propertyName = pn<School>("inactive");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.school.inactive)}</DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.school.inactive}
        onChange={(e) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.inactive = e.target.checked;
            })
          )
        }
      />
    );
  };

  const targetSchoolField = () => {
    const propertyName = pn<School>("isTargetSchool");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.school.isTargetSchool)}</DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.school.isTargetSchool}
        onChange={(e) =>
          props.setSchool(
            produce(props.school, (draft) => {
              draft.isTargetSchool = e.target.checked;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {nameField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {officialSchoolNameField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {schoolCodeField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {schoolTypeField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {ncesField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {actCodeField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {termTypeField()}
      </Grid>

      <Grid item xs={largeFieldBreakpoints}>
        {gradeLevelsField()}
      </Grid>

      <Grid item xs={fullFieldBreakpoints}>
        <AddressFields
          crudMode={props.crudMode}
          properties={properties.find((x) => x.propertyName === pn<School>("address"))?.children || []}
          address={props.school.address || ({} as Address)}
          setAddress={(address) =>
            props.setSchool(
              produce(props.school, (draft) => {
                draft.address = address;
              })
            )
          }
          validationErrors={props.validationErrors[pn<School>("address")] || {}}
        />
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {contactField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {isTitle1Field()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {serviceLocationOnlyField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {targetSchoolField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {inactiveField()}
      </Grid>
    </Grid>
  );
};
